import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';

import { useStudents } from '../../hooks/useStudents';
import { useSubjects } from '../../hooks/useSubjects';
import { useIntakes } from '../../hooks/useIntakes';
import { useEnrollments } from '../../hooks/useEnrollments';

const EnrollmentNew = () => {
  const navigate = useNavigate();
  const { id: contextStudentId } = useParams(); // present for /students/:id/enrollments/new
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const intakeQuery = params.get('intake') || '';

  const queryClient = useQueryClient();

  const { students, getStudentEnrollments } = useStudents();
  const { subjects } = useSubjects();
  const { intakes } = useIntakes();
  // We import both so this file works even before you update the hook to include addEnrollmentsBulk
  const { addEnrollmentsBulk, addEnrollment } = useEnrollments?.() || {};

  const [selectedStudentId, setSelectedStudentId] = useState(contextStudentId || '');
  const [selectedIntakeId, setSelectedIntakeId] = useState(intakeQuery || '');
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [studentEnrollments, setStudentEnrollments] = useState([]); // all enrollments for chosen student

  const lockedContext = Boolean(contextStudentId);

  // Load enrollments whenever the selected student changes (so we can prevent duplicates)
  const loadEnrollmentsForStudent = useCallback(
    async (studentId) => {
      if (!studentId) {
        setStudentEnrollments([]);
        return;
      }
      setLoadingEnrollments(true);
      setError(null);
      try {
        const res = await getStudentEnrollments(studentId);
        if (res?.success) {
          setStudentEnrollments(res.data || []);
        } else {
          setStudentEnrollments([]);
          setError(res?.error || 'Failed to load existing enrollments for this student.');
        }
      } catch (e) {
        console.error(e);
        setError('Unexpected error while loading enrollments.');
        setStudentEnrollments([]);
      } finally {
        setLoadingEnrollments(false);
      }
    },
    [getStudentEnrollments]
  );

  // On mount or when route param changes, pre-load in context
  useEffect(() => {
    if (contextStudentId) {
      setSelectedStudentId(contextStudentId);
      loadEnrollmentsForStudent(contextStudentId);
    }
    // If opened globally and intakeQuery exists, it’s already in state.
  }, [contextStudentId, loadEnrollmentsForStudent]);

  // If user selects a student from the global route
  const handleStudentChange = async (e) => {
    const val = e.target.value;
    setSelectedStudentId(val);
    // Keep ?intake= if present; otherwise reset intake
    setSelectedIntakeId(intakeQuery || '');
    setSelectedSubjectIds([]);
    if (val) {
      await loadEnrollmentsForStudent(val);
    } else {
      setStudentEnrollments([]);
    }
  };

  const handleIntakeChange = (e) => {
    setSelectedIntakeId(e.target.value);
    setSelectedSubjectIds([]); // reset subject selections when intake changes
  };

  const toggleSubject = (subjectId) => {
    setSelectedSubjectIds((prev) =>
      prev.includes(subjectId) ? prev.filter((id) => id !== subjectId) : [...prev, subjectId]
    );
  };

  // Compute subject_ids already enrolled for the selected intake
  const enrolledSubjectIdsForIntake = useMemo(() => {
    if (!selectedIntakeId) return new Set();
    const set = new Set(
      studentEnrollments
        .filter((en) => String(en.intake_id) === String(selectedIntakeId))
        .map((en) => String(en.subject_id))
    );
    return set;
  }, [studentEnrollments, selectedIntakeId]);

  const buildBackUrl = () => {
    const sid = selectedStudentId || contextStudentId;
    if (!sid) return '/enrollments';
    const base = `/students/${sid}?tab=enrollments`;
    return selectedIntakeId ? `${base}&intake=${selectedIntakeId}` : base;
  };

  const onCancel = () => {
    navigate(buildBackUrl(), { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!selectedStudentId) return setError('Please select a student.');
    if (!selectedIntakeId) return setError('Please select an intake.');

    // remove ones already enrolled in this intake
    const freshChoices = selectedSubjectIds.filter(
      (sid) => !enrolledSubjectIdsForIntake.has(String(sid))
    );
    if (freshChoices.length === 0) return setError('Please select at least one new subject.');

    setSubmitting(true);

    // --- Optimistic merge so StudentDetails shows updates instantly
    const optimisticNow = new Date().toISOString().split('T')[0];
    const intakeObj = intakes.find((i) => String(i.id) === String(selectedIntakeId)) || null;

    const optimisticRows = freshChoices.map((sid) => {
      const subj = subjects.find((s) => String(s.id) === String(sid)) || { id: sid };
      return {
        id: `temp_${Date.now()}_${sid}`,
        student_id: selectedStudentId,
        subject_id: sid,
        intake_id: selectedIntakeId,
        enrollment_date: optimisticNow,
        status: 'Active',
        // Joined objects for immediate rendering in StudentDetails (no N+1 lookups)
        intake: intakeObj
          ? { id: intakeObj.id, name: intakeObj.name, start_date: intakeObj.start_date, end_date: intakeObj.end_date }
          : null,
        subject: subj ? { id: subj.id, code: subj.code, name: subj.name } : null,
        __optimistic: true,
      };
    });

    queryClient.setQueryData(['enrollments', selectedStudentId], (prev = []) => {
      // De-dupe by intake+subject in case of quick double submit
      const seen = new Set(prev.map((r) => `${r.intake_id}:${r.subject_id}`));
      const toAdd = optimisticRows.filter((r) => !seen.has(`${r.intake_id}:${r.subject_id}`));
      return [...prev, ...toAdd];
    });

    // Navigate back immediately for “instant save”
    navigate(buildBackUrl(), { replace: true });

    try {
      const payload = freshChoices.map((sid) => ({
        student_id: selectedStudentId,
        subject_id: sid,
        intake_id: selectedIntakeId,
        enrollment_date: optimisticNow,
        status: 'Active',
      }));

      // ✅ Safe bulk create: prefer mutation.mutateAsync; fall back to function; else per-row helper
      let created = null;
      if (addEnrollmentsBulk?.mutateAsync) {
        created = await addEnrollmentsBulk.mutateAsync({ studentId: selectedStudentId, rows: payload });
      } else if (typeof addEnrollmentsBulk === 'function') {
        created = await addEnrollmentsBulk({ studentId: selectedStudentId, rows: payload });
      } else if (typeof addEnrollment === 'function') {
        const results = await Promise.all(payload.map((row) => addEnrollment(row)));
        const anyFail = results.some((r) => r?.success === false);
        if (anyFail) throw new Error('Some enrollments failed to create');
        created = results;
      } else {
        throw new Error('No enrollment creation function available.');
      }

      // Quiet refresh to reconcile with server truth
      queryClient.invalidateQueries({ queryKey: ['enrollments', selectedStudentId] });
      queryClient.prefetchQuery({
        queryKey: ['enrollments', selectedStudentId],
        staleTime: 60_000,
      });

      // Optional: you could toast success here if desired
      // e.g., toast.success('Enrollments created');
    } catch (err) {
      console.error(err);
      // Rollback optimistic rows on failure
      queryClient.setQueryData(['enrollments', selectedStudentId], (prev = []) =>
        prev.filter((r) => r.__optimistic !== true)
      );
      queryClient.invalidateQueries({ queryKey: ['enrollments', selectedStudentId] });
      alert('Some enrollments failed to create. The list has been refreshed.');
    } finally {
      setSubmitting(false);
    }
  };

  const lockedStudent = lockedContext
    ? students.find((s) => String(s.id) === String(contextStudentId))
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <h1 className="text-xl font-bold text-gray-900">Add Enrollments</h1>
      <Card>
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            {lockedContext
              ? 'You are enrolling subjects for this specific student. The student field is locked.'
              : 'Select a student, intake, and one or more subjects to enroll.'}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Student */}
            {!lockedContext && (
              <div>
                <label htmlFor="student" className="block text-sm font-medium text-gray-700 mb-1">
                  Student *
                </label>
                <select
                  id="student"
                  value={selectedStudentId}
                  onChange={handleStudentChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select a student</option>
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.full_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {lockedContext && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Student</label>
                <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-gray-700">
                  {lockedStudent ? lockedStudent.full_name : contextStudentId}
                </div>
              </div>
            )}

            {/* Intake */}
            <div>
              <label htmlFor="intake" className="block text-sm font-medium text-gray-700 mb-1">
                Intake *
              </label>
              <select
                id="intake"
                value={selectedIntakeId}
                onChange={handleIntakeChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
                disabled={!selectedStudentId}
              >
                <option value="">Select an intake</option>
                {intakes.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
              {selectedIntakeId && loadingEnrollments && studentEnrollments.length === 0 && (
                <p className="mt-2 text-sm text-gray-500">Checking existing enrollments…</p>
              )}

            </div>
          </div>

          {/* Subjects (checkbox grid) */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subjects *
            </label>

            {!selectedIntakeId ? (
              <p className="text-sm text-gray-500">Select an intake first to choose subjects.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {subjects.map((subject) => {
                  const subjectIdStr = String(subject.id);
                  const alreadyEnrolled = enrolledSubjectIdsForIntake.has(subjectIdStr);
                  const checked = selectedSubjectIds.includes(subject.id);
                  return (
                    <label
                      key={subject.id}
                      className={`flex items-center space-x-2 p-3 border rounded-md ${
                        alreadyEnrolled ? 'bg-gray-50 opacity-60 cursor-not-allowed' : 'bg-white'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        disabled={alreadyEnrolled}
                        checked={checked}
                        onChange={() => toggleSubject(subject.id)}
                      />
                      <span className="text-sm text-gray-800">
                        {subject.code} — {subject.name}
                        {alreadyEnrolled && (
                          <span className="ml-2 text-xs text-gray-500">(already enrolled)</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                submitting || !selectedStudentId || !selectedIntakeId || selectedSubjectIds.length === 0
              }
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Create Enrollments'}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default EnrollmentNew;
